<style scoped>
.br-10 {
  padding: 10px;
  border-radius: 10px;
}
</style>

<template>
  <v-container fluid fill-height>
    <v-layout justify-center>
      <v-flex xl4 lg6 md7 sm10 xs12>
        <v-form @submit.prevent="submitForm($event)" class="text-center">
          <v-text-field prepend-icon="fa-user-circle fa-lg" :error-messages="errors.collect('full_name')" clearable autofocus :value="data.full_name" @input="patchData({ full_name: $event })" :label="$t('full_name')" name="full_name" required type="text" v-validate="rules.full_name"></v-text-field>
          <v-text-field prepend-icon="fa-id-card fa-lg" :error-messages="errors.collect('ic_no')" clearable :data-vv-as="$t('ic_no')" :mask="'######-##-####'" name="ic_no" :value="icFormat(data.ic_num)" :counter="14" maxLength="14" @input="patchData({ ic_num: $event })" :label="$t('ic_no')" required type="text" return-masked-value v-validate="rules.ic_no"></v-text-field>
          <v-text-field prepend-icon="fa-phone-square fa-lg" :error-messages="errors.collect('phone_no')" :data-vv-as="$t('phone_no')" :label="$t('phone_no')" :value="data.phone" @input="patchData({ phone: $event })" name="phone_no" v-validate="rules.phone" type="number" required></v-text-field>
          <v-layout class="align-center justify-space-between" v-if="!$vuetify.breakpoint.xsOnly">
            <v-text-field prepend-icon="fa-envelope fa-lg" :error-messages="errors.collect('email_address')" :data-vv-as="$t('email_address')" :value="data.name" @input="patchData({ name: $event })" :label="$t('email_address')" name="email_address" required type="text" v-validate="rules.email_address" :disabled="!!hasName || verify.result"></v-text-field>
            <v-btn color="primary" rounded outlined :disabled="currentTime > 0 || !validateEmail(data.name) || data.full_name.length === 0" @click="sendVerification" v-if="!verify.result && !hasSession">
              {{ sent ? $t(`resend_verification`, { channel: $t('email_address') }) : $t('send_verification', { channel: $t('email_address') }) }} <span v-if="currentTime > 0">({{ currentTime }} s)</span>
            </v-btn>
          </v-layout>
          <v-text-field v-if="$vuetify.breakpoint.xsOnly" prepend-icon="fa-envelope fa-lg" :error-messages="errors.collect('email_address')" :data-vv-as="$t('email_address')" :value="data.name" @input="patchData({ name: $event })" :label="$t('email_address')" name="email_address" required type="text" v-validate="rules.email_address" :disabled="!!hasName || verify.result"></v-text-field>
          <v-btn color="primary" rounded outlined :disabled="currentTime > 0 || !validateEmail(data.name) || data.full_name.length === 0" @click="sendVerification" v-if="!verify.result && $vuetify.breakpoint.xsOnly && !hasSession">
            {{ sent ? $t(`resend_verification`, { channel: $t('email_address') }) : $t('send_verification', { channel: $t('email_address') }) }} <span v-if="currentTime > 0">({{ currentTime }} s)</span>
          </v-btn>
          <v-layout justify-space-between class="align-center" v-if="sent">
            <v-text-field prepend-icon="fa-lock fa-lg" validate-on-blur :rules="[() => !!verify.input || 'This field is required']" v-model="verify.input" :label="$t('code')" name="code" required type="text" :disabled="verify.result"></v-text-field>
            <v-btn color="primary" rounded outlined :disabled="verify.input.length === 0 || verify.result" @click="verifyCode">
              {{ $t("verify") }}
            </v-btn>
          </v-layout>
          <v-text-field ref="password" prepend-icon="fa-lock fa-lg" :error-messages="errors.collect('password')" :value="data.password" @input="patchData({ password: $event })" :label="$t('password')" name="password" required type="password" v-validate="rules.password"></v-text-field>
          <v-text-field validate-on-blur prepend-icon="fa-lock fa-lg" :error-messages="errors.collect('confirm_password')" :value="data.confirm_password" @input="patchData({ confirm_password: $event })" :label="$t('confirm_password')" name="confirm_password" required type="password" v-validate="'required|confirmed:password'"></v-text-field>
          <v-layout justify-center>
            <span class="primary--text caption pa-3"><strong>{{ $t('email_hint') }}</strong></span>
          </v-layout>
          <v-layout class="row justify-space-around" pt-2>
            <v-btn :block="$vuetify.breakpoint.xsOnly" color="red lighten-1" class="white--text" large rounded @click="$router.push('/')">{{ $t('cancel') }}</v-btn>
            <v-btn v-if="!hasSession" :block="$vuetify.breakpoint.xsOnly" color="primary" large rounded type="submit" :disabled="!verify.result" :loading="isLoading">{{ $t('register') }}</v-btn>
            <v-btn v-if="!!hasSession" :block="$vuetify.breakpoint.xsOnly" color="primary" large rounded @click.prevent="updateData()" :disabled="!!this.errors.any() || isLoading" :loading="isLoading">{{ $t('update') }}</v-btn>
          </v-layout>
        </v-form>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { throttle } from 'lodash'

export default {
  beforeRouteLeave (to, from, next) {
    this.resetData()
    next()
  },
  data: () => ({
    currentTime: 0,
    verify: {
      code: null,
      input: '',
      result: false
    },
    sent: false
  }),
  computed: {
    ...mapGetters({
      hasName: 'auth/hasName',
      hasSession: 'auth/hasSession',
      isLoading: 'form/isLoading'
    }),
    ...mapState({
      data: (state) => state.form.data,
      rules: (state) => state.form.rules
    }),
    isValid: function () {
      return Object.keys(this.fields).every((key) => this.fields[key].valid)
    }
  },
  async created () {
    if (this.$store.getters['auth/hasName']) {
      this.$store.commit('form/setId', { id: this.$store.getters['auth/hasName'] })
      await this.$store.dispatch('form/fetchData')
    }
  },
  methods: {
    ...mapActions({
      patchData: 'form/patchData',
      resetData: 'form/resetData',
      sendEmail: 'form/sendEmail',
      submitData: 'form/submitData',
      updateData: 'form/updateData'
    }),
    countdown () {
      if (this.currentTime > 0) {
        this.currentTime -= 1
        setTimeout(this.countdown, 1000)
      } else {
        this.currentTime = 0
        this.verify.code = Math.floor(100000 + Math.random() * 900000).toString()
      }
    },
    icFormat (value) {
      const format = value.replace(/[-\s]/g, '')
      if (format.length > 8) return `${format.substr(0, 6)}-${format.substr(6, 2)}-${format.substr(8)}`
      if (format.length > 6) return `${format.substr(0, 6)}-${format.substr(6, 2)}`
      return format
    },
    validateEmail (email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(String(email).toLowerCase())
    },
    sendVerification: throttle(async function () {
      this.currentTime = 180
      this.verify.code = Math.floor(100000 + Math.random() * 900000).toString()
      setTimeout(this.countdown, 1)
      this.sent = true
      await this.sendEmail({
        contact: this.data.name,
        message: `Hi ${this.data.full_name},<br/><br/>Your MPT e-Services Email Verification Code is<br/><br/><strong>${this.verify.code}</strong><br/><br/>for registration MPT e-Services account. The code will expires in 3 mins.<br/><br/>*Note: If you didn't request any email verification code, please ignore this message.`
      })
    }, 1000),
    verifyCode () {
      if (this.verify.code === this.verify.input) {
        this.verify.result = true
        this.$store.dispatch('snackbar/setSnackBar', {color: 'success', message: this.$t('verify_successful'), visible: true})
        return
      }
      this.verify.result = false
      this.$store.dispatch('snackbar/setSnackBar', {color: 'error', message: this.$t('invalid_code'), visible: true})
    },
    submitForm: async function ($event) {
      if (!(await this.$validator.validateAll())) return
      const response = await this.submitData()
      if (response && response._id) {
        this.$store.dispatch('snackbar/setSnackBar', { color: 'success', message: this.$t('registration_successful'), visible: true })
        this.$router.push({ name: 'home' })
      } else if (response.error && response.error === 'conflict') {
        this.currentTime = 0
        this.verify = {
          code: null,
          input: '',
          result: false
        }
        this.sent = false
        this.$validator.reset()
      } else {
        this.resetData()
        this.currentTime = 0
        this.verify = {
          code: null,
          input: '',
          result: false
        }
        this.sent = false
        this.$validator.reset()
      }
    }
  }
}
</script>
